/**
 * ReadingPane — the right side of the inbox. Shows the selected item in
 * full: why it needs the founder, the actual document (embedded invoice for
 * invoice-backed kinds, a payload detail grid otherwise), the decision
 * history, and a sticky Approve / Reject bar with an optional note.
 */
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Check, ExternalLink, ShieldAlert, X } from "lucide-react";
import { cn, type StatusTone } from "@flint/ui";
import {
  type CategoryTone,
  type InboxItem,
  kindCategory,
  kindLabel,
  requesterInitials,
  requesterLabel,
  snapshotChips,
  whyText,
} from "./inbox-model.ts";
import { EmbeddedInvoice } from "./embedded-invoice.tsx";
import { formatDateTime, formatRelativeTime } from "../../lib/format.ts";

interface ReadingPaneProps {
  item: InboxItem | null;
  isFounder: boolean;
  sellerName: string;
  onDecide: (decision: "approved" | "rejected", note: string) => void;
  decidePending: boolean;
}

const STATUS_CONFIG: Record<
  InboxItem["status"],
  { tone: StatusTone; label: string; dot: string }
> = {
  pending: { tone: "warn", label: "Pending your decision", dot: "bg-amber-400" },
  approved: { tone: "ok", label: "Approved", dot: "bg-emerald-500" },
  rejected: { tone: "bad", label: "Rejected", dot: "bg-rose-500" },
};

const AVATAR_BG: Record<CategoryTone, string> = {
  warn: "bg-amber-100 text-amber-700",
  bad: "bg-rose-100 text-rose-700",
  info: "bg-sky-100 text-sky-700",
};

const CALLOUT: Record<CategoryTone, { border: string; bg: string; icon: string }> = {
  warn: {
    border: "border-l-amber-400",
    bg: "bg-amber-50",
    icon: "text-amber-500",
  },
  bad: {
    border: "border-l-rose-500",
    bg: "bg-rose-50",
    icon: "text-rose-500",
  },
  info: {
    border: "border-l-sky-500",
    bg: "bg-sky-50",
    icon: "text-sky-600",
  },
};

export function ReadingPane({
  item,
  isFounder,
  sellerName,
  onDecide,
  decidePending,
}: ReadingPaneProps) {
  const [note, setNote] = useState("");

  useEffect(() => {
    setNote("");
  }, [item?.id]);

  if (!item) {
    return <EmptyState />;
  }

  const cat = kindCategory(item.kind);
  const callout = CALLOUT[cat.tone];
  const entityLink = entityLinkFor(item);
  const chips = snapshotChips(item.kind, item.requestPayload);
  const canAct = isFounder && item.status === "pending";
  const statusCfg = STATUS_CONFIG[item.status];

  return (
    <div className="flex h-full flex-col bg-bg">
      {/* ── Header ── */}
      <header className="flex shrink-0 items-start gap-4 border-b border-line bg-paper px-6 py-4">
        {/* Avatar */}
        <div
          className={cn(
            "mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg text-xs font-bold",
            AVATAR_BG[cat.tone],
          )}
        >
          {requesterInitials(item)}
        </div>

        <div className="min-w-0 flex-1">
          <h1 className="text-base font-bold leading-snug text-ink">{item.summary}</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            {/* Status dot + label */}
            <span className="flex items-center gap-1.5 text-xs font-medium text-ink-2">
              <span className={cn("size-2 rounded-full", statusCfg.dot)} />
              {statusCfg.label}
            </span>
            <span className="text-xs text-muted">{kindLabel(item.kind)}</span>
            <span className="text-xs text-muted">
              By {requesterLabel(item)} · {formatRelativeTime(item.createdAt)}
            </span>
          </div>
        </div>

        {entityLink ? (
          <Link
            to={entityLink.to}
            params={entityLink.params}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-line bg-paper px-3 text-xs font-medium text-ink-2 hover:bg-bg hover:text-ink"
          >
            <ExternalLink size={13} />
            Open
          </Link>
        ) : null}
      </header>

      {/* ── Body (scrolls) ── */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[720px] px-6 py-5 space-y-5">
          {/* Why this needs you */}
          <div
            className={cn(
              "flex gap-3 rounded-lg border-l-[3px] px-4 py-3.5",
              callout.border,
              callout.bg,
            )}
          >
            <ShieldAlert
              size={17}
              className={cn("mt-0.5 shrink-0", callout.icon)}
            />
            <div>
              <p className="mb-0.5 text-xs font-bold text-ink">Why this needs you</p>
              <p className="text-xs leading-relaxed text-ink-2">{whyText(item.kind)}</p>
            </div>
          </div>

          {/* Document / detail grid */}
          {item.entityType === "invoice" ? (
            <div>
              <SectionLabel>Invoice under review</SectionLabel>
              <EmbeddedInvoice invoiceId={item.entityId} sellerName={sellerName} />
            </div>
          ) : chips.length > 0 ? (
            <div>
              <SectionLabel>Request detail</SectionLabel>
              <div className="overflow-hidden rounded-lg border border-line bg-paper shadow-sm">
                {chips.map((c, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[160px_1fr] gap-4 border-b border-line px-5 py-3 text-sm last:border-b-0"
                  >
                    <div className="font-medium text-muted">{c.label}</div>
                    <div className={cn("font-semibold text-ink", c.strong && "text-sm")}>
                      {c.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Decision record (once decided) */}
          {item.status !== "pending" && item.decidedAt ? (
            <div>
              <SectionLabel>Decision</SectionLabel>
              <div className="rounded-lg border border-line bg-paper px-5 py-4 shadow-sm">
                <p className="text-sm text-ink-2">
                  <span className="font-semibold capitalize text-ink">{item.status}</span> by{" "}
                  {item.decidedByUserName ?? "unknown"} · {formatDateTime(item.decidedAt)}
                </p>
                {item.decisionNote ? (
                  <p className="mt-1.5 text-xs italic text-muted">
                    "{item.decisionNote}"
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Sticky decision bar ── */}
      {canAct ? (
        <div className="flex shrink-0 items-center gap-3 border-t border-line bg-paper px-6 py-3.5 shadow-[0_-6px_24px_-8px_rgba(15,23,42,0.08)]">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder="Add a note (optional) — printed on the audit row"
            className="h-9 min-w-0 flex-1 rounded-lg border border-line bg-bg px-3 text-xs text-ink-2 outline-none transition focus:border-sky-400 focus:bg-paper focus:ring-2 focus:ring-sky-100"
          />
          <button
            type="button"
            disabled={decidePending}
            onClick={() => onDecide("rejected", note.trim())}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-rose-200 bg-paper px-4 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:opacity-50"
          >
            <X size={13} strokeWidth={2.5} />
            {decidePending ? "Rejecting…" : "Reject"}
          </button>
          <button
            type="button"
            disabled={decidePending}
            onClick={() => onDecide("approved", note.trim())}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
          >
            <Check size={13} strokeWidth={2.5} />
            {decidePending ? "Approving…" : "Approve"}
          </button>
        </div>
      ) : item.status === "pending" && !isFounder ? (
        <div className="shrink-0 border-t border-line bg-paper px-6 py-3 text-xs text-muted">
          Awaiting the Founder's decision.
        </div>
      ) : null}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center bg-paper p-10 text-center">
      <svg
        width="160"
        height="130"
        viewBox="0 0 160 130"
        fill="none"
        aria-hidden="true"
        className="mb-5 opacity-70"
      >
        <circle cx="80" cy="65" r="55" fill="#f1f5f9" />
        <rect x="50" y="20" width="62" height="80" rx="8" fill="#e2e8f0" />
        <rect x="40" y="30" width="62" height="72" rx="8" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.5" />
        <rect x="52" y="46" width="38" height="5" rx="2.5" fill="#cbd5e1" />
        <rect x="52" y="58" width="28" height="5" rx="2.5" fill="#e2e8f0" />
        <rect x="52" y="70" width="32" height="5" rx="2.5" fill="#e2e8f0" />
        <circle cx="108" cy="88" r="18" fill="#94a3b8" />
        <path
          d="M100 88.5l5 5 10-10.5"
          stroke="#fff"
          strokeWidth="2.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <h2 className="text-base font-semibold text-ink">You're all caught up</h2>
      <p className="mt-1.5 max-w-[260px] text-xs leading-relaxed text-muted">
        Flagged actions land here for your decision — review and approve or reject in one place.
      </p>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2.5 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted">
      {children}
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

function entityLinkFor(
  item: InboxItem,
): { to: string; params: Record<string, string> } | null {
  if (item.entityType === "invoice") {
    return { to: "/sales/invoices/$invoiceId", params: { invoiceId: item.entityId } };
  }
  if (item.entityType === "customer") {
    return { to: "/sales/customers/$customerId", params: { customerId: item.entityId } };
  }
  return null;
}
